/*
 * Gift with purchase
 * Keeps exactly one gift line (marked with the `_gwp` property) in the cart while it
 * holds a qualifying product, and removes it otherwise. Qualification and gift lines
 * come from sections/gift-with-purchase.liquid, see snippets/gift-with-purchase.liquid.
 *
 * The gift is priced at zero by a Shopify "Buy X get Y" discount. If a gift line is
 * not free, the discount isn't applying: the gift is removed and not re-added this session.
 *
 * Other scripts can await window.triumphGwpSync() after changing the cart, before
 * rendering it, so the gift is already in (or out of) the cart they fetch.
 */
(function () {
  var config = window.triumphGwp;
  if (!config || window.triumphGwpSync) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CART_MUTATION = /\/cart\/(add|change|update|clear)\b/;
  var BLOCK_KEY = 'triumphGwpBlocked';
  var SYNC_DELAY = 300;
  var SYNC_TIMEOUT = 5000;

  var baseFetch = window.fetch;
  var blocked = false;
  var timer = null;
  var queued = null;
  var chain = Promise.resolve();

  function isBlocked() {
    if (blocked) return true;
    try {
      return sessionStorage.getItem(BLOCK_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function block() {
    blocked = true;
    try {
      sessionStorage.setItem(BLOCK_KEY, '1');
    } catch (e) {}
  }

  function request(path, body, headers) {
    var options = { headers: headers || { Accept: 'application/json' } };
    if (body) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    return baseFetch.call(window, root + path, options).then(function (response) {
      if (!response.ok) {
        var error = new Error('Gift with purchase: ' + path + ' failed (' + response.status + ')');
        error.status = response.status;
        throw error;
      }
      return response.json();
    });
  }

  function parseState(json) {
    try {
      return JSON.parse(json);
    } catch (e) {
      return null;
    }
  }

  function fetchState() {
    // No JSON Accept header here: with it, /cart ignores `sections` and returns the cart JSON
    return request('cart?sections=' + config.sectionId, null, {}).then(function (sections) {
      var html = new DOMParser().parseFromString(sections[config.sectionId] || '', 'text/html');
      var el = html.querySelector('[data-gwp-state]');
      return el ? parseState(el.textContent) : null;
    });
  }

  function signature(state) {
    if (!state) return '';
    return state.lines
      .map(function (line) {
        return line.key + ':' + line.quantity;
      })
      .sort()
      .join();
  }

  // Which gift lines to change, and whether a gift has to be added.
  function plan(state) {
    var keep = state.qualifies ? state.lines[0] || null : null;
    if (keep && keep.quantity === 1 && keep.final_line_price > 0) {
      block();
      keep = null;
    }

    var updates = null;
    state.lines.forEach(function (line) {
      var quantity = line === keep ? 1 : 0;
      if (line.quantity !== quantity) {
        updates = updates || {};
        updates[line.key] = quantity;
      }
    });

    return { updates: updates, add: state.qualifies && !keep && !isBlocked() };
  }

  // Applies the plan. Resolves true when the cart changed.
  function reconcile(state, verified) {
    if (!state) return Promise.resolve(false);
    var next = plan(state);
    if (!next.updates && !next.add) return Promise.resolve(false);

    var steps = next.updates ? request('cart/update.js', { updates: next.updates }) : Promise.resolve();
    if (next.add) {
      steps = steps.then(function () {
        return request('cart/add.js', {
          items: [{ id: config.variantId, quantity: 1, properties: { _gwp: config.label } }],
        }).catch(function (error) {
          // Rejected by Shopify (e.g. gift sold out): stop retrying for this session.
          if (error.status) block();
        });
      });
    }

    return steps.then(function () {
      // Check once more that the gift we kept or added is actually free.
      if (verified || !state.qualifies) return true;
      return fetchState()
        .then(function (after) {
          return reconcile(after, true);
        })
        .then(function () {
          return true;
        });
    });
  }

  function refresh() {
    if (typeof window.triumphUpdateCartBadge === 'function') window.triumphUpdateCartBadge();
    if (!config.isCartPage) return;
    // The cart page is server-rendered: reload only when its gift lines are out of date.
    return fetchState().then(function (state) {
      if (signature(state) !== renderedSignature) window.location.reload();
    });
  }

  function run() {
    return fetchState()
      .then(function (state) {
        return reconcile(state);
      })
      .then(function (changed) {
        if (changed) return refresh();
      });
  }

  // Runs one sync at a time; calls made while one is running share the next run.
  function sync() {
    clearTimeout(timer);
    if (!queued) {
      queued = chain.then(function () {
        queued = null;
        return run();
      });
      chain = queued.catch(function (error) {
        console.warn(error);
      });
    }
    return queued;
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sync, SYNC_DELAY);
  }

  function isCartMutation(method, url) {
    return String(method || 'GET').toUpperCase() === 'POST' && CART_MUTATION.test(String(url || ''));
  }

  // Resolves once the gift is in sync. Never rejects, and gives up waiting after SYNC_TIMEOUT.
  window.triumphGwpSync = function () {
    return Promise.race([
      sync().catch(function () {}),
      new Promise(function (resolve) {
        setTimeout(resolve, SYNC_TIMEOUT);
      }),
    ]);
  };

  // Sync after any cart change made by the theme, apps or other scripts.
  window.fetch = function (input, init) {
    var result = baseFetch.apply(window, arguments);
    var isRequest = typeof Request !== 'undefined' && input instanceof Request;
    var method = (init && init.method) || (isRequest ? input.method : 'GET');
    if (isCartMutation(method, isRequest ? input.url : input)) {
      result.then(schedule, function () {});
    }
    return result;
  };

  var xhrOpen = XMLHttpRequest.prototype.open;
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._gwpCartMutation = isCartMutation(method, url);
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this._gwpCartMutation) this.addEventListener('load', schedule);
    return xhrSend.apply(this, arguments);
  };

  // The state rendered with the page: sync straight away only if it's already out of date.
  var inlineEl = document.querySelector('[data-gwp-state]');
  var initial = inlineEl ? parseState(inlineEl.textContent) : null;
  var renderedSignature = signature(initial);
  if (initial) {
    var start = plan(initial);
    if (start.updates || start.add) sync();
  }
})();
